import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquarePlus, ShieldCheck, Sparkles, Send, CheckCircle2, Wallet } from "lucide-react";
import { CONTACT_EMAIL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const STEPS = [
  { icon: MessageSquarePlus, title: "Segnala", text: "Conosci qualcuno che cerca un prodotto o un servizio? Inserisci i suoi contatti e cosa gli serve." },
  { icon: Sparkles, title: "Categorizziamo", text: "Scegli la tipologia o lascia che l'AI te la suggerisca. Un nostro operatore verifica ogni segnalazione." },
  { icon: Send, title: "Segui l'esito", text: "La segnalazione viene inoltrata a chi può aiutare. Tu ne segui lo stato passo dopo passo." },
  { icon: Wallet, title: "Incassi", text: "Quando la segnalazione va a buon fine, incassi il compenso che l'azienda riconosce per quel servizio." },
];

const CtaButtons = ({ user }) => (
  <div className="flex flex-col gap-3 sm:flex-row">
    <Button asChild size="lg" className="h-12 rounded-full bg-[#F4C20D] px-7 text-base text-[#0F2D5E] hover:bg-[#E0B000]" data-testid="hero-cta-primary">
      <Link to={user ? "/dashboard" : "/registrati"}>
        {user ? "Nuova segnalazione" : "Inizia a segnalare"} <ArrowUpRight className="ml-1 h-5 w-5" />
      </Link>
    </Button>
    {!user && (
      <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-[#0F2D5E] px-7 text-base text-[#0F2D5E]" data-testid="hero-cta-login">
        <Link to="/accedi">Ho già un account</Link>
      </Button>
    )}
  </div>
);

export default function Landing() {
  const { user } = useAuth();
  return (
    <main data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-12 lg:pb-24 lg:pt-20">
          <div className="lg:col-span-7">
            <p className="rise text-xs font-semibold uppercase tracking-[0.2em] text-[#0F2D5E]">Il passaparola che vale</p>
            <h1 className="rise d1 mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Conosci qualcuno che cerca <span className="text-[#0F2D5E]">un servizio?</span>{" "}
              <span className="relative whitespace-nowrap text-[#E0A800]">Segnalalo.</span>
            </h1>
            <p className="rise d2 mt-6 max-w-xl text-base text-[#495850] md:text-lg">
              iosegnalo.net mette in contatto le persone che hanno un'esigenza con chi offre prodotti e servizi. Tu segnali, noi verifichiamo e inoltriamo.
            </p>
            <div className="rise d3 mt-9"><CtaButtons user={user} /></div>
            <div className="rise d4 mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#495850]">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#0F2D5E]" /> Solo con il consenso della persona</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#0F2D5E]" /> Verifica umana di ogni segnalazione</span>
            </div>
          </div>
          <div className="rise d2 relative lg:col-span-5">
            <div className="grain relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-[#0F2D5E] sm:aspect-[16/11] lg:aspect-[4/5]">
              <img src={HERO_IMG} alt="Stretta di mano" className="h-full w-full object-cover opacity-85 mix-blend-luminosity" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2D5E]/80 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/90 p-4 backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <img src="/brand/iosegnalo-simbolo.svg" alt="" className="h-10 w-10 object-contain" />
                  <div>
                    <p className="text-sm font-semibold">Mario ha segnalato un vicino</p>
                    <p className="text-xs text-[#75867E]">Energia e utenze · Inoltrata</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="come-funziona" className="border-y border-[#E2E5DC] bg-white" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Come funziona, in 4 passi</h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="group rounded-3xl bg-[#0F2D5E] p-7 text-white shadow-[0_12px_30px_-12px_rgba(15,45,94,0.5)] transition-transform duration-300 hover:-translate-y-1" data-testid={`step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="h-7 w-7 text-[#F4C20D]" />
                  <span className="font-mono text-sm font-bold text-[#F4C20D]">0{i + 1}</span>
                </div>
                <h3 className="mt-8 text-2xl font-extrabold text-white">{s.title}</h3>
                <p className="mt-3 text-sm font-bold leading-relaxed text-white">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grain relative overflow-hidden rounded-[2rem] bg-[#0F2D5E] px-6 py-12 text-white sm:px-12 lg:flex lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Una segnalazione richiede un minuto.</h2>
            <p className="mt-3 max-w-lg text-white/80">Registrati e invia la tua prima segnalazione oggi stesso.</p>
          </div>
          <Button asChild size="lg" className="mt-8 h-12 rounded-full bg-[#F4C20D] px-7 text-[#0F2D5E] hover:bg-[#E0B000] lg:mt-0" data-testid="bottom-cta">
            <Link to={user ? "/dashboard" : "/registrati"}>{user ? "Vai alle segnalazioni" : "Crea il tuo account"}</Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-[#E2E5DC] py-8 text-center text-sm text-[#75867E]">
        © {new Date().getFullYear()} iosegnalo.net · <a href={`mailto:${CONTACT_EMAIL}`} className="underline-offset-4 hover:underline" data-testid="footer-contact-email">{CONTACT_EMAIL}</a> · <Link to="/contatti" className="underline-offset-4 hover:underline">Contatti</Link>
      </footer>
    </main>
  );
}
